import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { DOCUMENTS_INFORMATION_API, FILE_MANAGEMENT_API } from "../../api";
import { setDocumentInformationData, setReloadDocumentsInformationData } from "../../slice";
import { useDocumentInfo } from "../../hooks";
import BaseCard from "../BaseCard";

const UploadeDocs = ({ hasEditAccess, disabled = false, pageErrors = {} }) => {
  const dispatch = useDispatch();
  const handleSubmitForm = useDocumentInfo({ hasEditAccess });
  const { userInfo } = useSelector((state) => state.UserCurrentState);
  const { data, reloadPage } = useSelector((state) => state.DocumentsInformation);

  const { refetch: getDemandsInformation } = useQuery(
    ["getDocumentsInformation"],
    () => DOCUMENTS_INFORMATION_API.GetDocumentsInformation(),
    {
      onSuccess: ({ data: documentsInformation }) => {
        if (documentsInformation.success && documentsInformation.data) {
          dispatch(setDocumentInformationData(documentsInformation.data));
        }
        dispatch(setReloadDocumentsInformationData(false));
      },
      staleTime: Infinity,
    }
  );

  const { data: uploadConfigs } = useQuery(
    ["getSystemConfig"],
    () => FILE_MANAGEMENT_API.GetSystemConfig(),
    {
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (reloadPage) getDemandsInformation();
  }, [reloadPage]);

  return (
    <form style={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
        <Box
          display="flex"
          flexDirection={{ xs: "column-reverse", sm: "row", md: "row" }}
          alignItems={{ xs: "flex-end", sm: "center", md: "center" }}
          justifyContent="flex-end"
          mb={4}
        >
          <Typography variant="h2" letterSpacing={0.5} textAlign="right">
            لطفا تصویر ها را با کیفیت بالا در جای خود بارگذاری نمایید.
          </Typography>
          <Typography variant="h1" ml={4}>
            مدارک
          </Typography>
        </Box>
        <Typography variant="h1" ml={4} mb={1}>
          تصویر شناسنامه
        </Typography>
        <Box
          display="flex"
          justifyContent={{ xs: "space-around", xl: "space-between" }}
          flexDirection={{
            xs: "column-reverse",
            sm: "column-reverse",
            md: "row",
          }}
          alignItems={{ xs: "center", sm: "center", md: "center" }}
          my={2}
          width="100%"
        >
          <Box
            display="flex"
            justifyContent={{ xs: "space-around", xl: "space-between" }}
            flexDirection={{
              xs: "column-reverse",
              sm: "row",
              md: "row",
            }}
            width={{ xs: "100%", sm: "100%", md: "50%" }}
            boxSizing="border-box"
            pl={{ xl: 10 }}
          >
            <BaseCard
              title="صفحه توضیحات"
              id="identificationDescriptionPage"
              uploadConfigs={uploadConfigs?.data.data}
              file={data.identificationDescriptionPage}
              filename={
                data.identificationDescriptionPage !== undefined
                  ? data.identificationDescriptionPage.fileName
                  : undefined
              }
              disabled={disabled}
              error={pageErrors["identificationDescriptionPageId"]}
            />
            <BaseCard
              title="صفحه فرزندان"
              id="identificationChildrenPage"
              uploadConfigs={uploadConfigs?.data.data}
              file={data.identificationChildrenPage}
              filename={
                data.identificationChildrenPage !== undefined
                  ? data.identificationChildrenPage.fileName
                  : undefined
              }
              disabled={disabled}
              error={pageErrors["identificationChildrenPageId"]}
            />
          </Box>
          <Box
            display="flex"
            justifyContent={{ xs: "space-around", xl: "space-between" }}
            flexDirection={{
              xs: "column-reverse",
              sm: "row",
              md: "row",
            }}
            boxSizing="border-box"
            pl={{ xl: 10 }}
            width={{ xs: "100%", sm: "100%", md: "50%" }}
          >
            <BaseCard
              title="صفحه ازدواج"
              id="identificationMarriagePage"
              uploadConfigs={uploadConfigs?.data.data}
              file={data.identificationMarriagePage}
              filename={
                data.identificationMarriagePage !== undefined
                  ? data.identificationMarriagePage.fileName
                  : undefined
              }
              disabled={disabled}
              error={pageErrors["identificationMarriagePageId"]}
            />
            <BaseCard
              title="صفحه اول"
              id="identificationFirstPage"
              uploadConfigs={uploadConfigs?.data.data}
              file={data.identificationFirstPage}
              filename={
                data.identificationFirstPage !== undefined
                  ? data.identificationFirstPage.fileName
                  : undefined
              }
              disabled={disabled}
              error={pageErrors["identificationFirstPageId"]}
            />
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="space-around"
          flexDirection={{
            xs: "column-reverse",
            sm: "column-reverse",
            md: "row",
          }}
          alignItems="center"
          my={2}
          width="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            width={{ xs: "100%", sm: "100%", md: "50%" }}
          >
            {parseInt(userInfo.genderTypeEnum) === 1 && (
              <>
                <Typography variant="h1" mb={3}>
                  تصویر کارت پایان خدمت یا معافیت
                </Typography>
                <Box
                  display="flex"
                  justifyContent={{ xs: "space-around", xl: "space-between" }}
                  flexDirection={{
                    xs: "column-reverse",
                    sm: "row",
                    md: "row",
                  }}
                  width="100%"
                  boxSizing="border-box"
                  pl={{ xl: 10 }}
                >
                  <BaseCard
                    title="تصویر پشت کارت"
                    id="militaryServiceCardBack"
                    uploadConfigs={uploadConfigs?.data.data}
                    file={data.militaryServiceCardBack}
                    filename={
                      data.militaryServiceCardBack !== undefined
                        ? data.militaryServiceCardBack.fileName
                        : undefined
                    }
                    disabled={disabled}
                    error={pageErrors["militaryServiceCardBackId"]}
                  />
                  <BaseCard
                    title="تصویر روی کارت"
                    id="militaryServiceCardOn"
                    uploadConfigs={uploadConfigs?.data.data}
                    file={data.militaryServiceCardOn}
                    filename={
                      data.militaryServiceCardOn !== undefined
                        ? data.militaryServiceCardOn.fileName
                        : undefined
                    }
                    disabled={disabled}
                    error={pageErrors["militaryServiceCardOnId"]}
                  />
                </Box>
              </>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            width={{ xs: "100%", sm: "100%", md: "50%" }}
          >
            <Typography variant="h1" mb={3}>
              تصویر کارت ملی
            </Typography>
            <Box
              display="flex"
              justifyContent={{ xs: "space-around", xl: "space-between" }}
              flexDirection={{
                xs: "column-reverse",
                sm: "row",
                md: "row",
              }}
              width="100%"
              boxSizing="border-box"
              pl={{ xl: 10 }}
            >
              <BaseCard
                title="تصویر پشت کارت"
                id="nationalCardBack"
                uploadConfigs={uploadConfigs?.data.data}
                file={data.nationalCardBack}
                filename={
                  data.nationalCardBack !== undefined ? data.nationalCardBack.fileName : undefined
                }
                disabled={disabled}
                error={pageErrors["nationalCardBackId"]}
              />
              <BaseCard
                title="تصویر روی کارت"
                id="nationalCardOn"
                uploadConfigs={uploadConfigs?.data.data}
                file={data.nationalCardOn}
                filename={
                  data.nationalCardOn !== undefined ? data.nationalCardOn.fileName : undefined
                }
                disabled={disabled}
                error={pageErrors["nationalCardOnId"]}
              />
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="space-around"
          flexDirection={{
            xs: "column-reverse",
            sm: "row",
            md: "row",
          }}
          my={2}
          width="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            width={{ xs: "100%", sm: "100%", md: "50%" }}
          >
            <Typography variant="h1">تصویر مدرک شغلی</Typography>
            <Typography variant="h2" my={3} textAlign="right">
              لطفا تصویر مدرک شغلی یا بیمه خود را بارگذاری نمایید.
            </Typography>
            <Box display="flex" justifyContent="flex-end" width="100%">
              <BaseCard
                title="تصویر مدرک"
                id="jobCertificate"
                uploadConfigs={uploadConfigs?.data.data}
                file={data.jobCertificate}
                filename={
                  data.jobCertificate !== undefined ? data.jobCertificate.fileName : undefined
                }
                disabled={disabled}
                error={pageErrors["jobCertificateId"]}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            width={{ xs: "100%", sm: "100%", md: "48%" }}
          >
            <Typography variant="h1">تصویر مدرک تحصیلی</Typography>
            <Typography
              variant="h2"
              my={3}
              width={{ xs: "100%", sm: "100%", md: "48%", lg: "95%", xl: "65%" }}
              textAlign="right"
            >
              لطفا تصوير مدرک تحصیلی خود را بارگذاری نمایید. (مدرک فارغ التحصیلی، کارت دانشجویی يا
              ریز نمرات)
            </Typography>
            <Box display="flex" justifyContent="flex-end" width="100%">
              <BaseCard
                title="تصویر مدرک"
                id="degreeCertificate"
                uploadConfigs={uploadConfigs?.data.data}
                file={data.degreeCertificate}
                filename={
                  data.degreeCertificate !== undefined ? data.degreeCertificate.fileName : undefined
                }
                disabled={disabled}
                error={pageErrors["degreeCertificateId"]}
              />
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          width={{ xs: "100%", sm: "100%", md: "50%" }}
        >
          <Typography variant="h1">تصویر متقاضی</Typography>
          <Typography variant="h2" my={3} textAlign="right">
            لطفا عکس جدید خود را بارگذاری نمایید. (با رعایت شئونات اسلامی)
          </Typography>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <BaseCard
              title="تصویر شخصی"
              id="userPhoto"
              uploadConfigs={uploadConfigs?.data.data}
              file={data.userPhoto}
              filename={data.userPhoto !== undefined ? data.userPhoto.fileName : undefined}
              disabled={disabled}
              error={pageErrors["userPhotoId"]}
            />
          </Box>
        </Box>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            mt: "2rem",
          }}
        >
          <Button
            type="button"
            variant="m2"
            onClick={() => handleSubmitForm("/personal-info/request")}
            sx={{ mr: 1 }}
          >
            قبلی
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default UploadeDocs;
